import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LANGUAGES, type Session } from "@shared/schema";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  LayoutGrid,
  CalendarDays,
  Globe,
  X,
  ExternalLink,
  Video,
} from "lucide-react";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
} from "date-fns";

type CalendarSession = Session & {
  classTitle: string;
  providerName: string;
  language: string;
  category: string | null;
  thumbnailUrl: string | null;
  recordingId: string | null;
};

const STATUS_CONFIG: Record<string, { label: string; className: string; dotClass: string }> = {
  LIVE: {
    label: "LIVE",
    className: "bg-destructive text-white",
    dotClass: "neon-dot neon-dot-live",
  },
  SCHEDULED: {
    label: "Upcoming",
    className: "bg-primary/15 text-primary",
    dotClass: "neon-dot neon-dot-upcoming",
  },
  ENDED: {
    label: "Ended",
    className: "bg-muted text-muted-foreground",
    dotClass: "bg-muted-foreground",
  },
};

export default function CalendarPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<"day" | "week" | "month">("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedSession, setSelectedSession] = useState<CalendarSession | null>(null);

  const { data: sessions, isLoading } = useQuery<CalendarSession[]>({
    queryKey: ["/api/calendar/sessions"],
  });

  const { data: activeLanguages } = useQuery<{ value: string; label: string; flag: string }[]>({
    queryKey: ["/api/languages/active"],
  });

  const filtered = useMemo(() => {
    if (!sessions) return [];
    return sessions.filter((s) => {
      if (selectedLanguage && s.language !== selectedLanguage) return false;
      return true;
    });
  }, [sessions, selectedLanguage]);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const liveCount = filtered.filter((s) => s.status === "LIVE").length;
  const scheduledCount = filtered.filter((s) => s.status === "SCHEDULED").length;

  function navigateWeek(dir: number) {
    setCurrentDate((d) => (dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1)));
  }

  function goToToday() {
    setCurrentDate(new Date());
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 cal-glass-toolbar px-3 py-3.5 sm:px-6" style={{ zIndex: 50 }}>
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-3.5">
            <div className="flex items-center gap-3">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight glow-text" data-testid="text-calendar-title">
                {t("calendar.schedule")}
              </h1>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {liveCount > 0 && (
                  <span className="flex items-center gap-1.5" data-testid="text-live-count">
                    <span className="neon-dot neon-dot-live" />
                    {liveCount} {t("calendar.live")}
                  </span>
                )}
                <span className="flex items-center gap-1.5" data-testid="text-scheduled-count">
                  <span className="neon-dot neon-dot-upcoming" />
                  {scheduledCount} {t("calendar.upcoming")}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="cal-segmented-control flex items-center p-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("day")}
                  className={`rounded-full h-7 px-3 text-xs transition-all duration-220 ${view === "day" ? "cal-segmented-active" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}
                  data-testid="button-view-day"
                >
                  <CalendarIcon className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">{t("calendar.day")}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("week")}
                  className={`rounded-full h-7 px-3 text-xs transition-all duration-220 ${view === "week" ? "cal-segmented-active" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}
                  data-testid="button-view-week"
                >
                  <CalendarDays className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">{t("calendar.week")}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("month")}
                  className={`rounded-full h-7 px-3 text-xs transition-all duration-220 ${view === "month" ? "cal-segmented-active" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}
                  data-testid="button-view-month"
                >
                  <LayoutGrid className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">{t("calendar.month")}</span>
                </Button>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5"
                  onClick={() => view === "day" ? setCurrentDate((d) => subDays(d, 1)) : view === "week" ? navigateWeek(-1) : setCurrentDate((m) => subMonths(m, 1))}
                  data-testid="button-cal-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToToday}
                  className="cal-glass-pill px-3 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  data-testid="button-cal-today"
                >
                  {t("calendar.today")}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5"
                  onClick={() => view === "day" ? setCurrentDate((d) => addDays(d, 1)) : view === "week" ? navigateWeek(1) : setCurrentDate((m) => addMonths(m, 1))}
                  data-testid="button-cal-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <h2 className="text-sm font-semibold text-center tracking-tight glow-text-sm" data-testid="text-cal-current-period">
                {view === "day"
                  ? format(currentDate, "EEEE, MMMM d, yyyy")
                  : view === "week"
                  ? `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`
                  : format(currentDate, "MMMM yyyy")
                }
              </h2>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedLanguage("")}
              className={`cal-glass-pill px-3 py-1 text-[11px] font-medium transition-all ${!selectedLanguage ? "cal-glass-pill-active text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="button-cal-filter-all"
            >
              {t("browse.all")}
            </button>
            {(activeLanguages || []).filter((l) => l.value !== "OTHER").map((lang) => (
              <button
                key={lang.value}
                onClick={() => setSelectedLanguage(lang.value === selectedLanguage ? "" : lang.value)}
                className={`cal-glass-pill px-3 py-1 text-[11px] font-medium flex items-center gap-1.5 transition-all ${selectedLanguage === lang.value ? "cal-glass-pill-active text-primary" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`button-cal-filter-${lang.value.toLowerCase()}`}
              >
                <Globe className="h-3 w-3" />
                <span className="hidden sm:inline">{lang.label}</span>
                <span className="sm:hidden">{lang.label.slice(0, 3)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 overflow-hidden">
          {view === "day" ? (
            <DayView
              day={currentDate}
              sessions={filtered}
              isLoading={isLoading}
              selectedSession={selectedSession}
              onSelectSession={setSelectedSession}
            />
          ) : view === "week" ? (
            <WeekView
              days={weekDays}
              sessions={filtered}
              isLoading={isLoading}
              selectedSession={selectedSession}
              onSelectSession={setSelectedSession}
            />
          ) : (
            <MonthView
              currentMonth={currentDate}
              sessions={filtered}
              isLoading={isLoading}
            />
          )}
        </div>

        {selectedSession && (view === "day" || view === "week") && (
          <>
            <div className="hidden md:block">
              <SessionPreview
                session={selectedSession}
                onClose={() => setSelectedSession(null)}
              />
            </div>
            <div className="md:hidden fixed inset-0 z-50 flex items-end" data-testid="modal-session-preview-mobile">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedSession(null)} data-testid="button-close-preview-overlay" />
              <div className="relative w-full max-h-[85vh] rounded-t-2xl overflow-hidden animate-in slide-in-from-bottom duration-200"
                style={{
                  background: "rgba(5, 8, 22, 0.96)",
                  borderTop: "1px solid rgba(59, 130, 246, 0.18)",
                  boxShadow: "0 -12px 48px rgba(0, 0, 0, 0.6), 0 0 80px rgba(59, 130, 246, 0.06) inset, 0 0 0 0.5px rgba(59, 130, 246, 0.1)",
                }}>
                <SessionPreviewMobile
                  session={selectedSession}
                  onClose={() => setSelectedSession(null)}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DayView({
  day,
  sessions,
  isLoading,
  selectedSession,
  onSelectSession,
}: {
  day: Date;
  sessions: CalendarSession[];
  isLoading: boolean;
  selectedSession: CalendarSession | null;
  onSelectSession: (s: CalendarSession | null) => void;
}) {
  const { t } = useTranslation();
  const dayKey = format(day, "yyyy-MM-dd");
  const today = isToday(day);

  const daySessions = useMemo(() => {
    return sessions
      .filter((s) => format(new Date(s.startsAt), "yyyy-MM-dd") === dayKey)
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }, [sessions, dayKey]);

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-3 py-4" data-testid="container-day-view">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <div
            className={[
              "inline-flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold shrink-0",
              today ? "bg-primary text-primary-foreground cal-day-halo" : "",
            ].join(" ")}
            style={
              today
                ? { boxShadow: "0 0 24px rgba(59, 130, 246, 0.35), 0 0 48px rgba(59, 130, 246, 0.12)" }
                : { background: "rgba(20, 30, 60, 0.5)", border: "1px solid rgba(59, 130, 246, 0.1)" }
            }
          >
            {format(day, "d")}
          </div>
          <div>
            <p className={["text-base font-semibold tracking-tight", today ? "text-primary" : ""].join(" ")}>
              {format(day, "EEEE")}
            </p>
            <p className="text-sm text-muted-foreground/60">{format(day, "MMMM d, yyyy")}</p>
          </div>
          <div className="ml-auto text-xs text-muted-foreground/50">
            {daySessions.length} {daySessions.length === 1 ? "session" : "sessions"}
          </div>
        </div>

        {daySessions.length === 0 ? (
          <div className="cal-glass-container flex flex-col items-center justify-center py-16 text-center">
            <CalendarIcon className="h-10 w-10 text-muted-foreground/20 mb-3" />
            <p className="text-sm text-muted-foreground/50 font-medium">{t("calendar.noSessions")}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {daySessions.map((session) => {
              const isLive = session.status === "LIVE";
              const isSelected = selectedSession?.id === session.id;
              const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
              const start = new Date(session.startsAt);
              const end = session.endsAt ? new Date(session.endsAt) : null;
              const thumb = session.thumbnailUrl || DEFAULT_THUMBNAIL;

              return (
                <button
                  key={session.id}
                  onClick={() => onSelectSession(isSelected ? null : session)}
                  className={[
                    "w-full text-left p-4 cursor-pointer transition-all",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                    isLive ? "cal-glass-event cal-glass-event-live" : "cal-glass-event",
                    isSelected ? "ring-1 ring-primary/40" : "",
                  ].join(" ")}
                  data-testid={`card-day-session-${session.id}`}
                >
                  <div className="flex gap-4">
                    <div className="shrink-0 w-20 text-center pt-1">
                      <p className="text-sm font-semibold">{format(start, "h:mm")}</p>
                      <p className="text-[10px] text-muted-foreground/60 uppercase">{format(start, "a")}</p>
                      {end && (
                        <p className="text-[10px] text-muted-foreground/40 mt-0.5">
                          {format(end, "h:mm a")}
                        </p>
                      )}
                    </div>
                    <div className="w-px self-stretch" style={{ background: "linear-gradient(to bottom, rgba(59, 130, 246, 0.2), transparent)" }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-semibold leading-tight line-clamp-2">
                          {session.classTitle}
                        </h4>
                        <Badge className={`shrink-0 text-[9px] px-2 py-0.5 ${cfg.className}`}>
                          <span className={`h-1.5 w-1.5 rounded-full mr-1 ${cfg.dotClass}`} />
                          {cfg.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-[8px] bg-primary/10">
                            {session.providerName?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <p className="text-xs text-muted-foreground/70 truncate">{session.providerName}</p>
                      </div>
                      {session.title && (
                        <p className="text-xs text-muted-foreground/50 mt-1 line-clamp-1">{session.title}</p>
                      )}
                    </div>
                    <div className="hidden sm:block shrink-0 w-16 h-12 rounded-lg overflow-hidden" style={{ border: "1px solid rgba(59, 130, 246, 0.08)" }}>
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function WeekView({
  days,
  sessions,
  isLoading,
  selectedSession,
  onSelectSession,
}: {
  days: Date[];
  sessions: CalendarSession[];
  isLoading: boolean;
  selectedSession: CalendarSession | null;
  onSelectSession: (s: CalendarSession | null) => void;
}) {
  const { t } = useTranslation();
  const sessionsByDay = useMemo(() => {
    const map = new Map<string, CalendarSession[]>();
    for (const day of days) {
      map.set(format(day, "yyyy-MM-dd"), []);
    }
    for (const s of sessions) {
      const key = format(new Date(s.startsAt), "yyyy-MM-dd");
      if (map.has(key)) {
        map.get(key)!.push(s);
      }
    }
    Array.from(map.entries()).forEach(([key, list]) => {
      list.sort((a: CalendarSession, b: CalendarSession) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
    });
    return map;
  }, [sessions, days]);

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="hidden md:grid grid-cols-7 gap-3 p-3">
          {days.map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-14 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-xl" />
            </div>
          ))}
        </div>
        <div className="md:hidden space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="hidden md:block h-full overflow-y-auto overflow-x-hidden p-3" style={{ scrollbarGutter: "stable" }}>
        <div className="cal-glass-container overflow-hidden">
          <div className="grid min-h-[500px]" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
            {days.map((day) => {
              const dayKey = format(day, "yyyy-MM-dd");
              const daySessions = sessionsByDay.get(dayKey) || [];
              const today = isToday(day);

              return (
                <div
                  key={dayKey}
                  className={`cal-glass-column flex flex-col min-w-0 overflow-hidden ${today ? "cal-today-glow" : ""}`}
                >
                  <div className={`cal-glass-header sticky top-0 px-2 py-3 text-center flex-shrink-0`} style={{ zIndex: 10 }}>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/60">
                      {format(day, "EEE")}
                    </div>
                    <div className={[
                      "mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-all",
                      today
                        ? "bg-primary text-primary-foreground cal-day-halo"
                        : "text-foreground/80",
                    ].join(" ")}
                      style={today ? { boxShadow: "0 0 20px rgba(59, 130, 246, 0.35), 0 0 48px rgba(59, 130, 246, 0.12)" } : undefined}
                    >
                      {format(day, "d")}
                    </div>
                  </div>

                  <div className="flex-1 p-2 space-y-2 overflow-hidden">
                    {daySessions.length === 0 && (
                      <div className="flex items-center justify-center h-16 text-[10px] text-muted-foreground/30 font-medium tracking-wide">
                        {t("calendar.noSessions")}
                      </div>
                    )}
                    {daySessions.map((session) => {
                      const isLive = session.status === "LIVE";
                      const isSelected = selectedSession?.id === session.id;
                      const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
                      const start = new Date(session.startsAt);
                      const end = session.endsAt ? new Date(session.endsAt) : null;

                      return (
                        <button
                          key={session.id}
                          onClick={() => onSelectSession(isSelected ? null : session)}
                          className={[
                            "w-full text-left p-2.5 cursor-pointer",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-0",
                            isLive ? "cal-glass-event cal-glass-event-live" : "cal-glass-event",
                            isSelected ? "cal-glass-event-selected" : "",
                          ].join(" ")}
                          data-testid={`card-cal-week-session-${session.id}`}
                        >
                          <div className="flex items-start gap-1.5">
                            <div className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${cfg.dotClass}`}
                              style={isLive ? { boxShadow: "0 0 8px rgba(255, 60, 60, 0.4)" } : cfg.dotClass.includes("bg-primary") ? { boxShadow: "0 0 8px rgba(59, 130, 246, 0.3)" } : undefined}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-[11px] font-semibold leading-tight line-clamp-2 text-foreground/90">
                                {session.classTitle}
                              </h4>
                              <p className="text-[10px] text-muted-foreground/70 mt-0.5 font-medium">
                                {format(start, "h:mm a")}
                                {end && ` - ${format(end, "h:mm a")}`}
                              </p>
                              <p className="text-[10px] text-muted-foreground/50 truncate mt-0.5">
                                {session.providerName}
                              </p>
                            </div>
                            {isLive && (
                              <span className="flex-shrink-0 text-[7px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/80 text-white tracking-wider"
                                style={{ boxShadow: "0 0 10px rgba(255, 60, 60, 0.3)" }}>
                                LIVE
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="md:hidden h-full overflow-y-auto px-3 py-3" data-testid="container-week-agenda">
        {days.map((day) => {
          const dayKey = format(day, "yyyy-MM-dd");
          const daySessions = sessionsByDay.get(dayKey) || [];
          const today = isToday(day);

          if (daySessions.length === 0 && !today) return null;

          return (
            <div key={dayKey} className="mb-4" data-testid={`agenda-day-${dayKey}`}>
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className={[
                  "inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold shrink-0",
                  today ? "bg-primary text-primary-foreground cal-day-halo" : "",
                ].join(" ")}
                  style={today ? { boxShadow: "0 0 20px rgba(59, 130, 246, 0.35)" } : { background: "rgba(20, 30, 60, 0.5)", border: "1px solid rgba(59, 130, 246, 0.1)" }}
                >
                  {format(day, "d")}
                </div>
                <div>
                  <p className={["text-sm font-semibold tracking-tight", today ? "text-primary" : ""].join(" ")}>
                    {format(day, "EEEE")}
                  </p>
                  <p className="text-xs text-muted-foreground/60">{format(day, "MMM d")}</p>
                </div>
              </div>

              {daySessions.length === 0 ? (
                <p className="text-xs text-muted-foreground/30 ml-12 font-medium">{t("calendar.noSessions")}</p>
              ) : (
                <div className="space-y-2 ml-2">
                  {daySessions.map((session) => {
                    const isLive = session.status === "LIVE";
                    const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
                    const start = new Date(session.startsAt);
                    const end = session.endsAt ? new Date(session.endsAt) : null;

                    return (
                      <button
                        key={session.id}
                        onClick={() => onSelectSession(session)}
                        className={[
                          "w-full text-left p-3.5 cursor-pointer",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                          isLive ? "cal-glass-event cal-glass-event-live" : "cal-glass-event",
                        ].join(" ")}
                        data-testid={`card-cal-agenda-session-${session.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col items-center shrink-0 pt-0.5">
                            <div className={`h-2.5 w-2.5 rounded-full ${cfg.dotClass}`}
                              style={isLive ? { boxShadow: "0 0 8px rgba(255, 60, 60, 0.4)" } : undefined}
                            />
                            <div className="w-px h-full mt-1" style={{ background: "linear-gradient(to bottom, rgba(59, 130, 246, 0.15), transparent)" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-semibold leading-tight line-clamp-2">
                                {session.classTitle}
                              </h4>
                              {isLive && (
                                <span className="shrink-0 text-[8px] font-bold px-2 py-0.5 rounded-full bg-destructive/80 text-white tracking-wider"
                                  style={{ boxShadow: "0 0 10px rgba(255, 60, 60, 0.3)" }}>
                                  LIVE
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground/70">
                              <Clock className="h-3 w-3 shrink-0" />
                              <span className="font-medium">
                                {format(start, "h:mm a")}
                                {end && ` - ${format(end, "h:mm a")}`}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground/50 mt-0.5">
                              {session.providerName}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {days.every((day) => {
          const dayKey = format(day, "yyyy-MM-dd");
          return (sessionsByDay.get(dayKey) || []).length === 0 && !isToday(day);
        }) && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarIcon className="mb-3 h-10 w-10 text-muted-foreground/20" />
            <p className="text-sm text-muted-foreground/50">{t("calendar.noSessions")}</p>
          </div>
        )}
      </div>
    </>
  );
}

function SessionPreview({
  session,
  onClose,
}: {
  session: CalendarSession;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isLive = session.status === "LIVE";
  const isEnded = session.status === "ENDED";
  const hasRecording = isEnded && !!session.recordingId;
  const thumbnailUrl = session.thumbnailUrl || DEFAULT_THUMBNAIL;
  const lang = LANGUAGES.find((l) => l.value === session.language);
  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
  const start = new Date(session.startsAt);
  const end = session.endsAt ? new Date(session.endsAt) : null;

  return (
    <div className="w-80 flex-shrink-0 cal-glass-sidebar flex flex-col overflow-hidden" data-testid="panel-session-preview">
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.1)" }}>
        <span className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-[0.15em] glow-text-sm">{t("calendar.preview")}</span>
        <Button size="icon" variant="ghost" onClick={onClose} className="h-6 w-6 rounded-full hover:bg-white/5" data-testid="button-close-preview">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="relative">
          <img
            src={thumbnailUrl}
            alt={session.classTitle}
            className="w-full aspect-video object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          {isLive && (
            <span className="absolute top-3 left-3 flex items-center gap-1 text-[8px] font-bold px-2 py-0.5 rounded-full bg-destructive/90 text-white tracking-wider"
              style={{ boxShadow: "0 0 12px rgba(255, 60, 60, 0.4)" }}>
              <LiveBroadcastIcon size={12} color="white" />
              LIVE NOW
            </span>
          )}
          {!isLive && (
            <Badge variant="secondary" className={`absolute top-3 left-3 text-[10px] ${cfg.className} no-default-hover-elevate`}>
              <span className={`mr-1 h-1.5 w-1.5 rounded-full ${cfg.dotClass}`} />
              {session.status === "LIVE" ? t("calendar.live") : session.status === "SCHEDULED" ? t("calendar.upcoming") : cfg.label}
            </Badge>
          )}
        </div>

        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-base font-bold leading-snug tracking-tight" data-testid="text-preview-title">
              {session.classTitle}
            </h3>
            {session.category && (
              <span className="inline-block mt-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.15)", color: "rgba(140, 180, 255, 0.8)" }}>
                {session.category}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs" style={{ background: "rgba(59, 130, 246, 0.15)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                {session.providerName?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium" data-testid="text-preview-provider">{session.providerName}</p>
              {lang && (
                <p className="text-[11px] text-muted-foreground/60 flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {lang.label}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2 text-sm" style={{
            background: "rgba(15, 20, 45, 0.5)",
            border: "1px solid rgba(59, 130, 246, 0.08)",
            borderRadius: "0.75rem",
            padding: "0.75rem",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
          }}>
            <div className="flex items-center gap-2 text-muted-foreground/70">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs font-medium">{format(start, "EEEE, MMM d, yyyy")}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground/70">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs font-medium">
                {format(start, "h:mm a")}
                {end && ` - ${format(end, "h:mm a")}`}
              </span>
            </div>
          </div>

          <div className="space-y-2 pt-1">
            {isLive && (
              <Link href={`/session/${session.id}`}>
                <Button className="w-full btn-gradient-capsule h-9" data-testid="button-preview-go">
                  <LiveBroadcastIcon size={15} className="mr-1" />
                  {t("calendar.joinLive")}
                </Button>
              </Link>
            )}

            {!isLive && (
              <Link href={`/sessions/${session.id}?from=calendar`}>
                <Button variant={hasRecording ? "outline" : "default"} className={`w-full rounded-xl h-9 ${!hasRecording ? "btn-gradient-capsule" : "btn-ghost-glass"}`} data-testid="button-preview-go">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  {t("calendar.viewDetails")}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionPreviewMobile({
  session,
  onClose,
}: {
  session: CalendarSession;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isLive = session.status === "LIVE";
  const isEnded = session.status === "ENDED";
  const hasRecording = isEnded && !!session.recordingId;
  const lang = LANGUAGES.find((l) => l.value === session.language);
  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
  const start = new Date(session.startsAt);
  const end = session.endsAt ? new Date(session.endsAt) : null;

  return (
    <div className="flex flex-col max-h-[85vh]" data-testid="panel-session-preview-mobile">
      <div className="flex items-center justify-center py-2">
        <div className="h-1 w-10 rounded-full" style={{ background: "rgba(59, 130, 246, 0.2)" }} />
      </div>

      <div className="flex items-center justify-between px-4 pb-2">
        <div className="flex items-center gap-2">
          {isLive ? (
            <span className="flex items-center gap-1 text-[8px] font-bold px-2 py-0.5 rounded-full bg-destructive/80 text-white tracking-wider"
              style={{ boxShadow: "0 0 10px rgba(255, 60, 60, 0.3)" }}>
              <LiveBroadcastIcon size={12} color="white" />
              LIVE NOW
            </span>
          ) : (
            <Badge className={`text-[10px] ${cfg.className} no-default-hover-elevate`}>
              <span className={`mr-1 h-1.5 w-1.5 rounded-full ${cfg.dotClass}`} />
              {session.status === "SCHEDULED" ? t("calendar.upcoming") : cfg.label}
            </Badge>
          )}
        </div>
        <Button size="icon" variant="ghost" onClick={onClose} className="h-7 w-7 rounded-full" data-testid="button-close-preview-mobile">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="overflow-y-auto px-4 pb-4 space-y-4">
        <div>
          <h3 className="text-lg font-bold leading-snug tracking-tight" data-testid="text-preview-title-mobile">
            {session.classTitle}
          </h3>
          {session.category && (
            <span className="inline-block mt-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.15)", color: "rgba(140, 180, 255, 0.8)" }}>
              {session.category}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs" style={{ background: "rgba(59, 130, 246, 0.15)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
              {session.providerName?.charAt(0)?.toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium">{session.providerName}</p>
            {lang && (
              <p className="text-xs text-muted-foreground/60 flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {lang.label}
              </p>
            )}
          </div>
        </div>

        <div style={{
          background: "rgba(15, 20, 45, 0.5)",
          border: "1px solid rgba(59, 130, 246, 0.08)",
          borderRadius: "0.75rem",
          padding: "0.75rem",
          boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
        }}>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground/70">
              <CalendarIcon className="h-4 w-4 shrink-0" />
              <span className="font-medium">{format(start, "EEEE, MMMM d, yyyy")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground/70">
              <Clock className="h-4 w-4 shrink-0" />
              <span className="font-medium">
                {format(start, "h:mm a")}
                {end && ` - ${format(end, "h:mm a")}`}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-1">
          {isLive && (
            <Link href={`/session/${session.id}`}>
              <Button className="w-full btn-gradient-capsule h-10" data-testid="button-preview-go-mobile">
                <LiveBroadcastIcon size={17} className="mr-1.5" />
                {t("calendar.joinLive")}
              </Button>
            </Link>
          )}

          {!isLive && (
            <Link href={`/sessions/${session.id}?from=calendar`}>
              <Button variant={hasRecording ? "outline" : "default"} className={`w-full rounded-xl h-10 ${!hasRecording ? "btn-gradient-capsule" : "btn-ghost-glass"}`} data-testid="button-preview-go-mobile">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t("calendar.viewDetails")}
              </Button>
            </Link>
          )}

          {hasRecording && (
            <Link href={`/recordings/${session.recordingId}`}>
              <Button variant="outline" className="w-full rounded-xl h-10" data-testid="button-preview-recording-mobile">
                <Video className="h-4 w-4 mr-2" />
                {t("calendar.watchRecording")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthView({
  currentMonth,
  sessions,
  isLoading,
}: {
  currentMonth: Date;
  sessions: CalendarSession[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const allDays = eachDayOfInterval({ start: calStart, end: calEnd });

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, CalendarSession[]>();
    for (const s of sessions) {
      const key = format(new Date(s.startsAt), "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [sessions]);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="grid grid-cols-7 gap-2">
          {allDays.map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="cal-glass-container overflow-hidden p-3">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((d) => (
            <div key={d} className="text-center text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/50 py-2">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {allDays.map((day) => {
            const dayKey = format(day, "yyyy-MM-dd");
            const daySessions = sessionsByDay.get(dayKey) || [];
            const inMonth = isSameMonth(day, currentMonth);
            const today = isToday(day);
            const hasLive = daySessions.some((s) => s.status === "LIVE");

            return (
              <div
                key={dayKey}
                className={`cal-glass-month-cell min-h-[80px] sm:min-h-[100px] p-1.5 ${!inMonth ? "opacity-30" : ""} ${today ? "cal-today-glow" : ""}`}
                style={today ? { borderColor: "rgba(59, 130, 246, 0.2)" } : undefined}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={[
                    "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold",
                    today ? "bg-primary text-primary-foreground cal-day-halo" : "text-foreground/70",
                  ].join(" ")}
                    style={today ? { boxShadow: "0 0 14px rgba(59, 130, 246, 0.35)" } : undefined}
                  >
                    {format(day, "d")}
                  </span>
                  {hasLive && (
                    <span className="neon-dot neon-dot-live" />
                  )}
                </div>
                <div className="space-y-0.5">
                  {daySessions.slice(0, 3).map((s) => {
                    const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.SCHEDULED;
                    return (
                      <Link key={s.id} href={s.status === "LIVE" ? `/session/${s.id}` : `/sessions/${s.id}`}>
                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] truncate cursor-pointer transition-all duration-200 hover:bg-primary/10 hover:translate-x-0.5"
                          style={{ border: "1px solid transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(59, 130, 246, 0.12)"; e.currentTarget.style.boxShadow = "0 0 8px rgba(59, 130, 246, 0.06)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.boxShadow = "none"; }}
                          data-testid={`card-cal-month-session-${s.id}`}
                        >
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${cfg.dotClass}`} />
                          <span className="truncate text-foreground/70 font-medium">{s.classTitle}</span>
                        </div>
                      </Link>
                    );
                  })}
                  {daySessions.length > 3 && (
                    <p className="text-[9px] text-muted-foreground/40 px-1 font-medium">
                      +{daySessions.length - 3} more
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
